/**
 * Display formatting.
 *
 * One module, because six screens showing dates three different ways is exactly
 * the inconsistency the shared component library exists to prevent.
 *
 * Everything here is display-only. Nothing in this file may be used to build a
 * value that is sent back to the API — the wire format is ISO throughout
 * (`shared/entities.ts`), and a formatted string that finds its way into a request
 * body is a silent data corruption.
 */

/** en-GB: `13 May 2026`. Unambiguous in a way that 05/13 versus 13/05 is not. */
const DATE_FORMAT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const DATE_TIME_FORMAT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * A calendar date (`YYYY-MM-DD`) or a timestamp, shown as a date.
 *
 * A bare `YYYY-MM-DD` is parsed by hand rather than handed to `new Date()`:
 * `new Date('2026-05-13')` is midnight *UTC*, which in a timezone behind UTC
 * renders as the 12th. Attendance dates and dates of birth are calendar facts
 * (§1.28), and a date that moves by a day depending on where the browser is
 * standing is not one.
 */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '—';

  const calendarDate = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (calendarDate) {
    const [, year, month, day] = calendarDate;
    return DATE_FORMAT.format(new Date(Number(year), Number(month) - 1, Number(day)));
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : DATE_FORMAT.format(parsed);
}

/**
 * When something runs, over days.
 *
 * A single-day activity or session has no end date, and reads as one date
 * rather than as a range with a missing half. The em dash matches
 * `formatTimeRange`, so a screen showing both reads consistently.
 */
export function formatDateRange(
  start: string | null | undefined,
  end: string | null | undefined
): string {
  if (!start) return formatDate(end);
  if (!end || end === start) return formatDate(start);
  return `${formatDate(start)} – ${formatDate(end)}`;
}

/** A timestamp with the time of day, for "recorded at" and audit columns. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '—' : DATE_TIME_FORMAT.format(parsed);
}

/**
 * `'10:00:00'`, `'12:30:00'` -> `'10:00 – 12:30'`.
 *
 * A `TIME` column comes back as `HH:MM:SS`; the seconds are always zero here and
 * showing them makes a schedule look like a stopwatch. Either end may be absent —
 * §3.17 allows an activity whose times are not yet fixed — so a lone start reads
 * as `'from 10:00'` rather than as a broken range.
 */
export function formatTimeRange(
  start: string | null | undefined,
  end: string | null | undefined
): string {
  const from = start ? start.slice(0, 5) : null;
  const to = end ? end.slice(0, 5) : null;

  if (from && to) return `${from} – ${to}`;
  if (from) return `from ${from}`;
  if (to) return `until ${to}`;
  return '—';
}

/** A person's name from the parts that may or may not be present (§3.38). */
export function formatName(person: {
  first_name: string;
  middle_name?: string | null;
  last_name?: string | null;
}): string {
  return [person.first_name, person.middle_name, person.last_name].filter(Boolean).join(' ');
}

/** A blank-safe cell value: `null`, `undefined` and `''` all read as an em dash. */
export function orDash(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const text = String(value).trim();
  return text === '' ? '—' : text;
}

/**
 * `UNDER_REVIEW` -> `Under review`. Staff never see a raw database value (§8.4.4).
 *
 * Tolerates a missing value and renders it as `orDash` would. TypeScript says
 * this cannot happen, and it happened anyway: adding `category` to the volunteer
 * meant every response predating the change had no such field, and a display
 * helper that throws on one absent string white-screens the whole profile. A
 * dash is a worse answer than the label; a blank page is a worse answer than
 * either.
 */
export function humaniseCode(code: string | null | undefined): string {
  if (code === null || code === undefined || code === '') return '—';
  const lower = code.replace(/_/g, ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * A table's heading with the number of records in it — "Volunteers (47)".
 *
 * The count is the server's `total`, not the length of the page on screen: a
 * heading reading "(10)" above ten of forty-seven rows is worse than no count
 * at all. Undefined while the first page is still loading, and then the bare
 * title is shown rather than a flickering "(0)".
 */
export function countedTitle(title: string, total: number | undefined): string {
  return total === undefined ? title : `${title} (${total})`;
}
